import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import FlexRow from '../../../../../molecules/FlexRow'
import Loading from '../../../../../molecules/Loading'
import { provisionedTargets } from '../../../../../../api/integrations'

const sourceLabels = {
  partitionKey: 'Partition Key',
  domainKey: 'Domain Key',
  changeTracking: 'Change Tracking',
  system: 'System'
}

const useStyles = makeStyles((theme) => ({
  body: {
    fontSize: '0.875rem',
    lineHeight: 1.6
  },
  infoBanner: {
    padding: theme.layout.padding.p10,
    background: 'var(--color-surface-secondary)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    marginBottom: '12px'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '0.75rem',
    fontWeight: 600
  },
  hashRow: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: 'var(--color-text-secondary)',
    marginTop: '4px',
    marginBottom: '8px'
  },
  ddlBlock: {
    background: 'var(--color-surface-secondary)',
    borderRadius: 'var(--border-radius-sm)',
    padding: '8px 12px',
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    marginBottom: '8px'
  },
  previewSection: {
    marginTop: '12px',
    padding: theme.layout.padding.p10,
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-sm)'
  },
  warningItem: {
    fontSize: '0.8125rem',
    padding: '4px 0',
    color: 'var(--color-warning-700)'
  },
  label: {
    fontWeight: 500,
    display: 'block',
    marginBottom: '4px',
    marginTop: '12px'
  },
  breakdownTable: {
    width: '100%',
    fontSize: '0.8125rem',
    borderCollapse: 'collapse',
    marginTop: '4px',
    '& th': {
      textAlign: 'left',
      fontWeight: 600,
      padding: '4px 8px',
      borderBottom: 'var(--border-neutral-thin)',
      fontSize: '0.75rem',
      color: 'var(--color-text-secondary)',
      textTransform: 'uppercase'
    },
    '& td': {
      padding: '4px 8px',
      borderBottom: '1px solid var(--color-surface-secondary)'
    }
  },
  sourceTag: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '0.6875rem',
    fontWeight: 600,
    background: 'var(--color-surface-secondary)',
    color: 'var(--color-text-secondary)'
  },
  redundantTag: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '0.6875rem',
    fontWeight: 600,
    background: 'var(--color-warning-100)',
    color: 'var(--color-warning-700)',
    marginLeft: '6px'
  }
}))

function getStatusColor (status) {
  switch (status) {
    case 'up_to_date': return 'var(--color-success-500)'
    case 'out_of_sync': return 'var(--color-warning-500)'
    case 'not_exists': return 'var(--color-danger-500)'
    case 'locked': return 'var(--color-text-secondary)'
    default: return 'var(--color-text-secondary)'
  }
}

function getStatusLabel (status) {
  switch (status) {
    case 'up_to_date': return 'Up to date'
    case 'out_of_sync': return 'Migration pending'
    case 'not_exists': return 'Table does not exist'
    case 'locked': return 'Locked'
    default: return status
  }
}

function SchemaBreakdown ({ breakdown, classes }) {
  if (!breakdown) return null
  const { autoColumns, userColumns } = breakdown
  const hasRedundant = userColumns.some(c => c.isRedundant)

  return (
    <div style={{ marginTop: '12px' }}>
      <label style={{ fontWeight: 500, fontSize: '0.8125rem', display: 'block', marginBottom: '6px' }}>
        Table structure
      </label>
      <table className={classes.breakdownTable}>
        <thead>
          <tr>
            <th>Column</th>
            <th>Type</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {autoColumns.map((c, i) => (
            <tr key={`auto-${i}`}>
              <td><code>{c.name}</code></td>
              <td>{c.type}</td>
              <td><span className={classes.sourceTag}>{sourceLabels[c.source] || c.source}</span></td>
            </tr>
          ))}
          {userColumns.map((c, i) => (
            <tr key={`user-${i}`}>
              <td>
                <code>{c.name}</code>
                {c.isRedundant && <span className={classes.redundantTag}>redundant</span>}
              </td>
              <td>{c.type} <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>({c.path})</span></td>
              <td><span className={classes.sourceTag}>Virtual Column</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasRedundant && (
        <div
          style={{
            padding: '8px 12px',
            borderLeft: '3px solid var(--color-warning-500)',
            background: 'var(--color-surface-secondary)',
            borderRadius: 'var(--border-radius-sm)',
            marginTop: '8px',
            fontSize: '0.8125rem'
          }}
        >
          Columns marked <strong>redundant</strong> are already included as physical columns
          from the partition key or domain key. Defining them again in columns creates duplicate
          columns in the DDL. Consider removing them from the columns list.
        </div>
      )}
    </div>
  )
}

SchemaBreakdown.propTypes = {
  breakdown: PropTypes.shape({
    autoColumns: PropTypes.array,
    userColumns: PropTypes.array,
    redundantColumns: PropTypes.array
  }),
  classes: PropTypes.object.isRequired
}

function MigrationPreview ({ result, classes }) {
  const { status, needsMigration, summary, safety, migrations, hashes, partitions, schemaBreakdown } = result

  return (
    <div className={classes.previewSection}>
      <FlexRow style={{ gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
        <span
          className={classes.statusBadge}
          style={{ backgroundColor: getStatusColor(status), color: '#fff' }}
        >
          {getStatusLabel(status)}
        </span>
        <span style={{ fontSize: '0.875rem' }}>{summary}</span>
      </FlexRow>

      {hashes && (
        <div className={classes.hashRow}>
          Configured: <code>{hashes.configured ? `${hashes.configured.slice(0, 12)}...` : '(none)'}</code>
          {' | '}Stored: <code>{hashes.stored ? `${hashes.stored.slice(0, 12)}...` : '(none)'}</code>
        </div>
      )}

      {safety && !safety.safe && (
        <div
          style={{
            padding: '8px 12px',
            borderLeft: '3px solid var(--color-danger-500)',
            background: 'var(--color-surface-secondary)',
            borderRadius: 'var(--border-radius-sm)',
            marginBottom: '8px'
          }}
        >
          <strong style={{ color: 'var(--color-danger-500)' }}>Data loss risk detected</strong>
        </div>
      )}

      {safety?.warnings?.length > 0 && (
        <div style={{ marginBottom: '8px' }}>
          {safety.warnings.map((w, i) => (
            <div key={i} className={classes.warningItem}>{w}</div>
          ))}
        </div>
      )}

      <SchemaBreakdown breakdown={schemaBreakdown} classes={classes} />

      {needsMigration && migrations?.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <label style={{ fontWeight: 500, fontSize: '0.8125rem', display: 'block', marginBottom: '6px' }}>
            DDL statements ({migrations.length})
          </label>
          {migrations.map((m, i) => (
            <div key={i} className={classes.ddlBlock}>
              <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '0.6875rem', textTransform: 'uppercase', color: 'var(--color-text-secondary)' }}>
                {m.operation}
              </div>
              {m.ddl}
            </div>
          ))}
        </div>
      )}

      {partitions?.pending?.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <label style={{ fontWeight: 500, fontSize: '0.8125rem', display: 'block', marginBottom: '6px' }}>
            Partitions to add ({partitions.pending.length})
          </label>
          {partitions.pending.map((p, i) => (
            <div key={i} style={{ fontSize: '0.8125rem', padding: '2px 0' }}>
              <code>{p.partitionName}</code> (connection {p.connectionId})
            </div>
          ))}
        </div>
      )}

      {!needsMigration && (
        <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
          No migrations will be applied.
        </div>
      )}
    </div>
  )
}

MigrationPreview.propTypes = {
  result: PropTypes.shape({
    status: PropTypes.string,
    needsMigration: PropTypes.bool,
    summary: PropTypes.string,
    safety: PropTypes.shape({
      safe: PropTypes.bool,
      warnings: PropTypes.arrayOf(PropTypes.string),
      dataLossRisk: PropTypes.bool
    }),
    migrations: PropTypes.arrayOf(PropTypes.shape({
      operation: PropTypes.string,
      ddl: PropTypes.string
    })),
    hashes: PropTypes.shape({
      configured: PropTypes.string,
      stored: PropTypes.string
    }),
    partitions: PropTypes.shape({
      existing: PropTypes.number,
      pending: PropTypes.array
    }),
    schemaBreakdown: PropTypes.shape({
      autoColumns: PropTypes.array,
      userColumns: PropTypes.array,
      redundantColumns: PropTypes.array
    })
  }).isRequired,
  classes: PropTypes.object.isRequired
}

function CheckMigrationsForm ({ target, onClose }) {
  const classes = useStyles()
  const { mutateAsync: previewMigration } = provisionedTargets.usePreviewProvisionedTargetMigration()
  const { mutateAsync: applyMigration } = provisionedTargets.useApplyProvisionedTargetMigration()

  const [previewResult, setPreviewResult] = useState(null)
  const [applyResult, setApplyResult] = useState(null)
  const [confirmingDataLoss, setConfirmingDataLoss] = useState(false)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState(null)

  const loadPreview = useCallback(async () => {
    try {
      const result = await previewMigration({
        provisionedTargetTableId: target.provisionedTargetTableId
      })
      setPreviewResult(result)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load migration preview')
    }
  }, [previewMigration, target])

  useEffect(() => {
    loadPreview()
  }, [loadPreview])

  const handleApply = useCallback(async () => {
    if (previewResult?.safety?.dataLossRisk && !confirmingDataLoss) {
      setConfirmingDataLoss(true)
      return
    }
    setError(null)
    setApplying(true)
    try {
      const result = await applyMigration({
        provisionedTargetTableId: target.provisionedTargetTableId
      })
      setApplyResult(result)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to apply migration')
      setConfirmingDataLoss(false)
    } finally {
      setApplying(false)
    }
  }, [applyMigration, target, previewResult, confirmingDataLoss])

  const isLoading = !previewResult && !error

  return (
    <>
      {isLoading && !applyResult && (
        <div className={classes.body}>
          <Loading />
          <p style={{ textAlign: 'center', marginTop: '8px' }}>Loading migration preview&hellip;</p>
        </div>
      )}

      {!previewResult && !applyResult && error && (
        <div className={classes.body}>
          <div
            className={classes.infoBanner}
            style={{ borderLeft: '3px solid var(--color-danger-500)' }}
          >
            {error}
          </div>
        </div>
      )}

      {previewResult && !applyResult && (
        <div className={classes.body}>
          <MigrationPreview result={previewResult} classes={classes} />

          {confirmingDataLoss && (
            <div
              className={classes.infoBanner}
              style={{ borderLeft: '3px solid var(--color-danger-500)', marginTop: '12px' }}
            >
              This migration involves <strong>data loss risk</strong>. Are you sure you want
              to proceed? Click &ldquo;Apply Migration&rdquo; again to confirm.
            </div>
          )}

          {error && (
            <div
              className={classes.infoBanner}
              style={{ borderLeft: '3px solid var(--color-danger-500)', marginTop: '12px' }}
            >
              {error}
            </div>
          )}
        </div>
      )}

      {applyResult && (
        <div className={classes.body}>
          <div
            className={classes.infoBanner}
            style={{ borderLeft: '3px solid var(--color-success-500)' }}
          >
            Migration applied successfully.
          </div>
          {applyResult.result && (
            <div style={{ marginTop: '12px', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              {applyResult.result}
            </div>
          )}
        </div>
      )}

      <SydModalActions>
        <SydButton variant='outline' size='sm' onClick={onClose}>
          {applyResult ? 'Done' : 'Close'}
        </SydButton>

        {previewResult?.needsMigration && !applyResult && (
          <SydButton
            variant='primary'
            size='sm'
            onClick={handleApply}
            disabled={applying}
            processing={applying}
          >
            {applying ? 'Applying...' : 'Apply Migration'}
          </SydButton>
        )}
      </SydModalActions>
    </>
  )
}

CheckMigrationsForm.propTypes = {
  target: PropTypes.object.isRequired,
  onClose: PropTypes.func
}

export default CheckMigrationsForm
